import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import UserService from "../../services/user";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { sanitizeReturnPath } from "../../utils/sponsorResume";
import { GoogleLogin } from "@react-oauth/google";

const LoginForm = ({ setShowRegister, prefillEmail = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPressed, setIsPressed] = useState(false);

  const goAfterLogin = () => {
    navigate(sanitizeReturnPath(location.state?.from), { replace: false });
  };

  const validationSchema = yup.object({
    email: yup.string("Enter email").email().required("Email is required"),
    password: yup.string("Enter password").required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: prefillEmail,
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // Try mentor login first
        let loginData = await UserService.loginUser(values);
        if (loginData.data.token) {
          localStorage.setItem("loginToken", loginData.data.token);
          toast.success("Login successfully");
          goAfterLogin();
          return;
        } else if (!loginData.data.is_verified) {
          toast.success(loginData.data.message);
          // Trigger OTP modal if needed
          return;
        }
      } catch (err) {
        // If mentor login fails, try super admin login
        try {
          let loginData = await UserService.superAdminLogin(values);
          if (loginData.data.token) {
            localStorage.setItem("loginToken", loginData.data.token);
            toast.success("Login successfully");
            goAfterLogin();
            return;
          } else if (!loginData.data.is_verified) {
            toast.success(loginData.data.message);
            // Trigger OTP modal if needed
            return;
          }
        } catch (err2) {
          toast.error("Please check your credentials");
        }
      }
    },
  });

  const handleCreateAccountClick = () => {
    setShowRegister(true);
  };

  const handleLoginWithGoogle = async (credentialResponse) => {
    try {
      const res = await UserService.loginUserWithGoogle({
        token: credentialResponse.credential,
      });

      if (res.data.token) {
        localStorage.setItem("loginToken", res.data.token);
        toast.success("Login successfully");
        goAfterLogin();
      }
      console.log("Backend response:", res.data);
    } catch (err) {
      toast.error("Login failed");
      console.error("Login failed:", err.response?.data || err.message);
    }
  };

  return (
    <>
      <style jsx>{`
        .login-container {
          min-height: 100vh;
          background-color: #f9fafb;
          display: flex;
          flex-direction: column;
        }

        @media (min-width: 1025px) {
          .login-container {
            flex-direction: row;
          }
        }

        .left-panel {
          width: 100%;
          background-color: #ffffff;
          position: relative;
          min-height: 50vh;
          overflow: hidden;
        }

        @media (min-width: 1025px) {
          .left-panel {
            width: 50%;
            min-height: 100vh;
          }
        }

        /* Image visibility control */
        .desktop-image {
          display: none;
        }

        .mobile-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
        }

        /* For smaller phones, use contain to show full image */
        @media (max-width: 480px) and (max-height: 700px) {
          .mobile-image {
            object-fit: contain;
          }
        }

        /* For medium phones, use cover to fill space */
        @media (min-width: 481px) and (max-width: 768px) {
          .mobile-image {
            object-fit: cover;
          }
        }

        /* For larger phones, use contain to show full image */
        @media (min-width: 769px) and (max-width: 1024px) {
          .mobile-image {
            object-fit: contain;
          }
        }

        @media (min-width: 1025px) {
          .desktop-image {
            display: block;
          }

          .mobile-image {
            display: none;
          }
        }

        .right-panel {
          width: 100%;
          background: #d0efe1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 2rem;
          min-height: 50vh;
        }

        @media (min-width: 1025px) {
          .right-panel {
            width: 50%;
            min-height: 100vh;
          }
        }

        @media (max-width: 1025px) {
          .left-panel {
            min-height: 40vh;
            padding: 0;
          }

          .right-panel {
            min-height: 60vh;
            padding: 2rem 1rem;
          }
        }

        .content-wrapper {
          max-width: 24rem;
          text-align: center;
          width: 100%;
        }

        .logo-container {
          margin-bottom: 2rem;
        }

        @media (max-width: 768px) {
          .logo-container {
            margin-bottom: 1rem;
          }
        }

        .logo {
          width: 12rem;
          height: auto;
          margin: 0 auto;
          display: block;
        }

        @media (max-width: 1025x) {
          .logo {
            width: 8rem;
          }
        }

        .description {
          color: #6b7280;
          font-size: 1rem;
          line-height: 1.6;
        }

        @media (max-width: 1025px) {
          .description {
            display: none;
          }
        }

        .welcome-title {
          font-size: 2rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .welcome-subtitle {
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 2rem;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .input-wrapper {
          position: relative;
        }

        .input-field {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          background-color: #ffffff;
          border-radius: 9999px;
          border: 1px solid #e5e7eb;
          outline: none;
          font-size: 1rem;
          transition: all 0.2s;
          box-sizing: border-box;
        }

        .input-field:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .error {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          margin-left: 1rem;
        }

        .btn-connect {
          width: 100%;
          background-color: #1f2937;
          color: #ffffff;
          padding: 1rem;
          border-radius: 9999px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          font-size: 1rem;
          margin-top: 1.5rem;
        }

        .btn-connect:hover {
          background-color: #374151;
        }

        .create-account {
          text-align: center;
          margin-top: 1rem;
          color: #6b7280;
          cursor: pointer;
          font-size: 1rem;
          transition: color 0.2s;
        }

        .create-account:hover {
          color: #1f2937;
        }

        .google-button {
          width: 100%;
          background-color: #ffffff;
          color: #374151;
          padding: 1rem;
          border-radius: 9999px;
          border: 1px solid #e5e7eb;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .google-button:hover {
          background-color: #f9fafb;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        .google-button:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        /* Mobile responsive styles for Google button */
        @media (max-width: 768px) {
          .google-login-container {
            width: 60% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            margin: 0 auto !important;
          }

          .google-login-container .google-login-button {
            max-width: 250px !important;
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }
        }

        @media (max-width: 480px) {
          .google-login-container {
            width: 70% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            margin: 0 auto !important;
          }

          .google-login-container .google-login-button {
            max-width: 200px !important;
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }
        }

        label {
          width: 100%;
        }
        .or-seprator {
          width: 100%;
          border-bottom: 2px solid #264653;
          position: relative;
          padding: 20px;
          margin-top: -30px;
        }
        .or-seprator span {
          background: #d0efe1;
          position: absolute;
          right: 0;
          left: 0;
          margin: 10px auto;
          display: inline-block;
          width: 50px;
          text-align: center;
        }
      `}</style>

      <div className="login-container">
        {/* Left Panel - Background Image */}
        <div className="left-panel">
          <img
            src="/LoginDesktop.png"
            alt="Login Background"
            className="desktop-image"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
          <img
            src="/LoginMobile.jpg"
            alt="Login Background"
            className="mobile-image"
          />
        </div>

        {/* Right Panel - Login Form */}
        <div className="right-panel">
          <div className="content-wrapper">
            {/* Welcome Text */}
            <div style={{ textAlign: "center" }}>
              <h1 className="welcome-title">Hello Again!</h1>
              <h2 className="welcome-subtitle">Welcome Back</h2>
            </div>

            {/* User Type Selection */}
            {/* No user type selection, just email and password */}

            {/* Login Form - Using your exact working code structure */}
            <form onSubmit={formik.handleSubmit} className="login-form">
              <label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="email"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.email && formik.errors.email && (
                  <div className="error">{formik.errors.email}</div>
                )}
              </label>

              <label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    placeholder="Password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="password"
                    className="input-field"
                    required
                  />
                  <div className="input-icon">
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className="error">{formik.errors.password}</div>
                )}
              </label>

              <button
                type="submit"
                onMouseDown={() => setIsPressed(true)}
                onMouseUp={() => setIsPressed(false)}
                onMouseLeave={() => setIsPressed(false)}
                style={{
                  marginLeft: "1px",
                  backgroundColor: isPressed ? "#299d8e" : "#264653",
                  color: isPressed ? "#fdc513" : "#ffffff",
                  border: "none",
                  outline: "none",
                  borderRadius: "49px",
                  margin: "10px 0",
                  cursor: "pointer",
                  transition: "0.5s",
                  width: "100%",
                  padding: "1rem",
                  fontWeight: "600",
                  fontSize: "1rem",
                  marginTop: "1.5rem",
                }}
              >
                Login
              </button>

              <p className="or-seprator">
                <span>OR</span>
              </p>
              <div
                className="google-login-container"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "80%",
                  margin: "0 auto",
                  minHeight: "48px",
                }}
              >
                <div className="google-login-button">
                  <GoogleLogin
                    onSuccess={handleLoginWithGoogle}
                    onError={() => console.log("Login Failed")}
                    theme="outline"
                    size="large"
                    text="signin_with"
                    shape="pill"
                    width="300"
                    useOneTap={false}
                    style={{
                      width: "100%",
                      maxWidth: "300px",
                      height: "48px",
                      borderRadius: "9999px",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "inherit",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  />
                </div>
              </div>
            </form>

            {/* Forgot Password */}
            <p onClick={handleCreateAccountClick} className="create-account">
              Create a new Account?
            </p>

            {/* Google Sign In
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="google-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Sign In with Google</span>
            </button> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
